import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import * as React from 'react';
import { Input } from '@/components/ui/field';
import { cn } from '@/lib/utils';

/**
 * SearchSelect — combo com campo de busca visível.
 *
 * Existe pelo mesmo motivo registrado no playbook: o `Select` do Radix tem
 * typeahead por tecla, mas não campo de busca visível — e a partir de algumas
 * dezenas de opções digitar faz parte do fluxo. Regras herdadas: teto de 100
 * itens no DOM, Enter escolhe o primeiro resultado, Esc fecha.
 */

export interface OpcaoBusca {
  valor: string;
  rotulo: string;
  /** Texto secundário à direita (versão, papel…). */
  detalhe?: string;
  desabilitada?: boolean;
  /** Motivo da opção estar indisponível — exibido, nunca escondido. */
  motivo?: string;
}

const TETO_DOM = 100;

export function SearchSelect({
  id,
  valor,
  aoMudar,
  opcoes,
  placeholder,
  placeholderBusca = 'Buscar…',
  vazio = 'Nenhum resultado para esta busca.',
  describedBy,
  disabled,
}: {
  id: string;
  valor: string | null;
  aoMudar: (valor: string) => void;
  opcoes: OpcaoBusca[];
  placeholder: string;
  placeholderBusca?: string;
  vazio?: string;
  describedBy?: string;
  disabled?: boolean;
}) {
  const [aberto, setAberto] = React.useState(false);
  const [busca, setBusca] = React.useState('');

  const filtradas = React.useMemo(() => {
    const termo = busca.trim().toLowerCase();
    const base = termo
      ? opcoes.filter((opcao) => `${opcao.rotulo} ${opcao.detalhe ?? ''}`.toLowerCase().includes(termo))
      : opcoes;
    return base.slice(0, TETO_DOM);
  }, [busca, opcoes]);

  const selecionada = opcoes.find((opcao) => opcao.valor === valor) ?? null;

  function escolher(opcao: OpcaoBusca) {
    if (opcao.desabilitada) return;
    aoMudar(opcao.valor);
    setAberto(false);
    setBusca('');
  }

  return (
    <PopoverPrimitive.Root open={aberto} onOpenChange={setAberto}>
      <PopoverPrimitive.Trigger asChild>
        <button
          id={id}
          type="button"
          role="combobox"
          aria-expanded={aberto}
          aria-describedby={describedBy}
          disabled={disabled}
          className="flex h-10 w-full items-center justify-between gap-2 rounded-input border border-border-strong bg-bg-surface px-3 text-body text-on-surface transition-colors duration-fast ease-standard hover:border-primary disabled:cursor-not-allowed disabled:bg-bg-subtle disabled:text-on-surface-muted"
        >
          <span className={cn('truncate', !selecionada && 'text-on-surface-muted')}>
            {selecionada ? selecionada.rotulo : placeholder}
          </span>
          <ChevronsUpDown className="size-4 shrink-0 text-icon" aria-hidden="true" />
        </button>
      </PopoverPrimitive.Trigger>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={4}
          className="z-50 w-[var(--radix-popover-trigger-width)] overflow-hidden rounded-input border border-border bg-bg-surface shadow-md duration-base ease-standard data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95"
        >
          <div className="border-b border-border p-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-icon" aria-hidden="true" />
              <Input
                autoFocus
                value={busca}
                placeholder={placeholderBusca}
                aria-label={placeholderBusca}
                onChange={(evento) => setBusca(evento.target.value)}
                onKeyDown={(evento) => {
                  if (evento.key === 'Enter') {
                    evento.preventDefault();
                    const primeira = filtradas.find((opcao) => !opcao.desabilitada);
                    if (primeira) escolher(primeira);
                  }
                }}
                className="h-9 pl-8"
              />
            </div>
          </div>

          <ul role="listbox" aria-label={placeholder} className="cmp-scroll-area max-h-64 overflow-y-auto p-1">
            {filtradas.length === 0 ? (
              <li className="px-3 py-6 text-center text-body text-on-surface-muted">{vazio}</li>
            ) : (
              filtradas.map((opcao) => {
                const ativa = opcao.valor === valor;
                return (
                  <li key={opcao.valor}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={ativa}
                      disabled={opcao.desabilitada}
                      title={opcao.motivo}
                      onClick={() => escolher(opcao)}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-chip px-2 py-2 text-left text-body transition-colors duration-fast ease-standard',
                        opcao.desabilitada ? 'cursor-not-allowed text-on-surface-muted' : 'hover:bg-bg-subtle',
                      )}
                    >
                      <Check className={cn('size-4 shrink-0 text-primary', !ativa && 'invisible')} aria-hidden="true" />
                      <span className="flex-1 truncate">{opcao.rotulo}</span>
                      {opcao.detalhe ? (
                        <span className="shrink-0 text-label font-normal text-on-surface-muted">{opcao.detalhe}</span>
                      ) : null}
                    </button>
                  </li>
                );
              })
            )}
            {opcoes.length > TETO_DOM ? (
              <li className="px-3 py-2 text-label font-normal text-on-surface-muted">
                Mostrando {TETO_DOM} de {opcoes.length}. Refine a busca para ver o resto.
              </li>
            ) : null}
          </ul>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
